import * as React from "react";

// components
import { SegmentedBar } from "@components/segmented-bar";
import { InfoTip } from "@components/tool-tip";

// time-allocation imports
import { IProps, IState } from "./TimeAllocation.d";
import * as s from "./TimeAllocation.styles";

const labels = {
  FOCUS_TIME: "Focus Time",
  MEETINGS: "meetings",
  OTHER: "other events",
  FRAGMENTED_TIME: "fragmented time",
};

///////////////
// Component //
///////////////

export class TimeAllocation extends React.Component<IProps, IState> {
  static defaultProps = {
    animateIn: true,
  };

  constructor(props: IProps) {
    super(props);
  }

  private getRoundedHours = (minutes: number) => {
    if (minutes > 600) {
      // if over 10, round to nearest integer
      return Math.round(minutes / 60);
    } else {
      // otherwise round to the nearest .5
      return Math.round(minutes / 30) / 2;
    }
  };

  private getSegments = (minutes: number, unit: string, color: string) => {
    const { totalMinutes } = this.props;

    const hours = this.getRoundedHours(minutes);
    const label = `${hours} hr ${unit}`;
    const remainder = totalMinutes ? totalMinutes - minutes : 1;

    return [
      { color, value: minutes, tipLabel: label },
      { color: s.barBg, value: remainder, isInset: true },
    ];
  };

  ///////////////
  // Rendering //
  ///////////////

  private renderJudgement = (score: number, goal: number, wantToBeatGoal?: boolean) => {
    if (this.props.hideJudgement) {
      return null;
    }

    const { totalMinutes } = this.props;
    const percentile = (score - goal) / (totalMinutes - goal);

    if (wantToBeatGoal) {
      if (score < goal) {
        return <div style={s.badLabel}>Low</div>;
      } else if (percentile < 0.3) {
        return <div style={s.goodLabel}>Ok</div>;
      } else if (percentile < 0.6) {
        return <div style={s.goodLabel}>Good</div>;
      } else {
        return <div style={s.goodLabel}>Great</div>;
      }
    } else {
      if (score > goal) {
        return <div style={s.badLabel}>High</div>;
      } else if (percentile > -0.05) {
        return <div style={s.goodLabel}>Ok</div>;
      } else if (percentile > -0.1) {
        return <div style={s.goodLabel}>Good</div>;
      } else {
        return <div style={s.goodLabel}>Great</div>;
      }
    }
  };

  private renderSegmentedBar = (
    minutes: number,
    goalMinutes: number,
    label: string,
    color: string,
    description: string,
  ) => {
    const { totalMinutes } = this.props;
    const showGoal =
      label === labels.FOCUS_TIME || label === labels.FRAGMENTED_TIME || totalMinutes > 0;

    const goalPercent = (goalMinutes / totalMinutes) * 100;
    const goalStyle = {
      ...s.goalIndicator,
      left: `${goalPercent}%`,
      display: showGoal ? "block" : "none",
    };

    const segments = this.getSegments(minutes, label, color);

    return (
      <div style={s.container}>
        <div style={s.legendRow}>
          <div style={s.focusHours}>{this.getRoundedHours(segments[0].value)} hr</div>
          <div>{label}</div>
          <div>
            <InfoTip text={description} iconStyle={s.tooltipIcon} tipType={"info"} />
          </div>
          {showGoal ? this.renderJudgement(minutes, goalMinutes, label === labels.FOCUS_TIME) : ""}
        </div>
        <div style={s.barContainer}>
          <div style={goalStyle} />
          <SegmentedBar segments={segments} animateIn={this.props.animateIn} />
        </div>
      </div>
    );
  };

  private renderFocusTime = () => {
    const { focusMinutes } = this.props;
    return this.renderSegmentedBar(
      focusMinutes,
      800,
      labels.FOCUS_TIME,
      s.focusColor,
      "Uninterrupted blocks of free time longer than 2 hours and within your working hours.",
    );
  };

  private renderBusyTime = () => {
    const { busyMinutes } = this.props;
    return this.renderSegmentedBar(
      busyMinutes,
      800,
      labels.MEETINGS,
      s.busyColor,
      "Calendar events with other attendees, or that our algorithm has identified is a meeting.",
    );
  };

  private renderUnclassifiedTime = () => {
    const { unclassifiedMinutes } = this.props;

    if (unclassifiedMinutes === undefined) {
      return null;
    }

    return this.renderSegmentedBar(
      unclassifiedMinutes,
      800,
      labels.OTHER,
      s.otherColor,
      "Calendar events that Clockwise has identified are not meetings e.g. personal holds.",
    );
  };

  private renderFragmentedTime = () => {
    const { fragmentedMinutes } = this.props;
    return this.renderSegmentedBar(
      fragmentedMinutes,
      360,
      labels.FRAGMENTED_TIME,
      s.fragmentedColor,
      "Blocks of free time shorter than 2 hours and within your working hours.",
    );
  };

  public render() {
    return (
      <React.Fragment>
        {this.renderFocusTime()}
        {this.renderFragmentedTime()}
        {this.renderBusyTime()}
        {this.renderUnclassifiedTime()}
      </React.Fragment>
    );
  }
}
